<script setup lang="ts">
import { useAppStore } from '@/stores/AppStore'
import { Ref, computed, onMounted, ref } from 'vue'
import {
  useCourseRepository,
  formatSellCourseWithQuota,
} from '@/repositories/courseRepo'
import { useBranchRepository } from '@/repositories/branchRepo'
import { useRouter } from 'vue-router'

// components
import { showNotify, Dialog } from 'vant'
import LoadingDialog from '@components/LoadingDialog.vue'
import { t } from 'i18next'

// types
import { ClinicBranch } from '@/type/Clinic'
import { CourseLineSellFormated } from '@/type/Course'

// inject repositories
const router = useRouter()
const appStore = useAppStore()
const branchRepository = useBranchRepository(appStore.clinic?.uuid!)
const courseRepository = useCourseRepository(appStore.clinic?.uuid!)

// data
const showDialog = ref(false)
const localSelectedBranch = ref<ClinicBranch>()
const branchList = ref<ClinicBranch[]>()
const view: Ref<'list' | 'table'> = ref('list')
const search = ref('')
const courseLoading = ref(false)
const course = ref<CourseLineSellFormated[]>([])

// Event Handler
const onSelectBranch = (branch: ClinicBranch) => {
  appStore.setSelectedBranch(branch)
}

const onConfirmBranch = () => {
  if (!appStore.selectedBranch) {
    showNotify({
      type: 'warning',
      message: t('course.buy_course.please_select_branch_warning'),
    })
    showDialog.value = true
    return
  }
  localSelectedBranch.value = appStore.selectedBranch!
  showDialog.value = false
  fetchCourseList()
}

const filtered_course = computed(() => {
  if (search.value) {
    return course.value.filter(course => {
      if (
        course.name.toLowerCase().includes(search.value.toLowerCase()) ||
        course.liff_data.cost_on_LINE.toString().includes(search.value)
      ) {
        return course
      }
    })
  }

  return course.value
})

const onClickCourse = (course_uuid: string) => {
  router.push({
    name: 'CourseSellDetail',
    params: { course_uuid: course_uuid },
  })
}

// Fetch Method
const fetchBranchList = async () => {
  try {
    const response = await branchRepository.fetchBranchList('sell_branch')
    return response
  } catch (error) {
    console.error(error)
    showNotify({
      type: 'danger',
      message: 'Failed to fetch branch list',
    })
  }
}
const fetchCourseList = async () => {
  try {
    courseLoading.value = true
    const { sell_course, patient_course_quota } =
      await courseRepository.fetchSellCourseList(
        localSelectedBranch.value?.uuid!,
      )
    course.value = formatSellCourseWithQuota(sell_course, patient_course_quota)
    appStore.setGlobalSellCourse(course.value)
  } catch (error) {
    showNotify({
      type: 'danger',
      message: 'Failed to fetch course list',
    })
    console.error(error)
  }
  courseLoading.value = false
}
onMounted(async () => {
  branchList.value = await fetchBranchList()
  if (appStore.selectedBranch) {
    localSelectedBranch.value = appStore.selectedBranch
    showDialog.value = false
  } else {
    showDialog.value = true
  }
  fetchCourseList()
})
</script>

<template>
  <LoadingDialog v-if="!appStore.clinic" :show="true" />

  <div v-else>
    <TopNavBar
      :title="appStore.clinic?.line_bot_profile_name ?? appStore.clinic?.name"
      :color="appStore.clinic?.color"
      :logo="appStore.clinic?.line_bot_profile_image_url"
    />
    <Dialog
      v-model:show="showDialog"
      :title="$t('course.buy_course.select_branch_title')"
    >
      <template #default>
        <div class="tw-mt-2">
          <div
            v-for="branch in branchList"
            :key="branch.uuid"
            class="tw-py-2 tw-cursor-pointer hover:tw-bg-gray-100"
            :class="[
              appStore.selectedBranch?.uuid === branch.uuid
                ? 'tw-bg-gray-100'
                : '',
            ]"
          >
            <div class="tw-px-4 tw-py-2" @click="onSelectBranch(branch)">
              <van-icon name="location-o" size="18" color="black" />
              {{ branch.text }}
            </div>
          </div>
        </div>
      </template>

      <template #footer>
        <div
          class="tw-btn tw-text-blue-500 tw-px-4 tw-py-2 tw-text-center tw-border-t tw-border-black-500"
          @click="onConfirmBranch"
        >
          {{ $t('submit') }}
        </div>
      </template>
    </Dialog>

    <div
      class="tw-flex tw-gap-2 tw-p-4 tw-items-center tw-pt-[clamp(5rem,8vh+env(safe-area-inset-top,0px),6rem)]"
    >
      <div class="tw-flex">{{ $t('course.buy_course.service_branch') }}</div>
      <div class="tw-flex-1">
        <div
          class="tw-btn tw-rounded-md tw-px-4 tw-py-2 tw-border tw-flex tw-justify-between"
          @click="showDialog = true"
          :style="{
            borderColor: appStore.clinic?.color,
            color: appStore.clinic?.color,
            backgroundColor: '#ffffff',
          }"
        >
          <span>{{ localSelectedBranch?.text }}</span>
          <span class="mdi mdi-swap-horizontal-circle-outline"> </span>
        </div>
      </div>
    </div>
    <!-- search bar and toolbar -->
    <div class="tw-px-2">
      <van-row align="center" justify="space-around" class="search text-center">
        <van-col span="3">
          <van-icon name="search" size="30" color="black" />
        </van-col>

        <van-col span="21">
          <van-field
            v-model="search"
            name="search"
            :placeholder="$t('course.search_course')"
          />
        </van-col>
      </van-row>

      <van-row align="center" class="list-header text-center tw-pt-2">
        <van-col span="16">
          <h3 class="tw-pl-2" style="--margin: 0px">
            {{ $t('course.buy_course.course_list') }}
          </h3>
        </van-col>

        <van-col span="4"
          ><van-icon
            :color="view === 'list' ? 'red' : 'black'"
            name="bars"
            size="20"
            @click="view = 'list'"
        /></van-col>
        <van-col span="4"
          ><van-icon
            :color="view === 'table' ? 'red' : 'black'"
            name="apps-o"
            size="20"
            @click="view = 'table'"
        /></van-col>
      </van-row>
    </div>
    <!-- course list -->

    <div v-if="!localSelectedBranch">
      <h3 class="tw-text-center tw-mt-4" style="color: #999999">
        {{ $t('course.buy_course.please_select_branch') }}
      </h3>
    </div>
    <div v-else-if="courseLoading">
      <van-loading style="margin-top: 20px" size="30px" color="#0094ff" vertical
        >Loading...</van-loading
      >
    </div>

    <div v-else-if="course.length <= 0">
      <h3 class="tw-text-center tw-mt-4" style="color: #999999">
        {{ $t('course.buy_course.no_course_list') }}
      </h3>
    </div>
    <div
      v-else-if="view === 'list'"
      style="height: calc(100vh - 174px); overflow: auto"
      :class="view === 'list' ? 'active' : 'inactive'"
      class="tw-pb-[clamp(5rem,8vh+env(safe-area-inset-top,0px),6rem)]"
    >
      <van-list v-for="item in filtered_course" :key="item.uuid">
        <!-- {{ item }} -->
        <van-card
          @click="onClickCourse(item.uuid + '')"
          currency="฿"
          :price="item.coursebranch.line_price"
          :title="item.name"
          :desc="item.description || ''"
          :thumb="item.liff_data.image_url"
        />
        <van-divider style="margin-top: 5px; margin-bottom: 5px"></van-divider>
      </van-list>
    </div>

    <div
      v-else-if="view === 'table'"
      style="height: calc(100vh - 174px); overflow: auto"
      class="tw-pb-[clamp(5rem,8vh+env(safe-area-inset-top,0px),6rem)]"
    >
      <van-grid clickable :column-num="2" :gutter="20">
        <van-grid-item
          @click="onClickCourse(item.uuid + '')"
          v-for="item in filtered_course"
          :key="item.uuid"
        >
          <van-image :src="item.liff_data.image_url" height="150" fit="cover" />
          <span style="margin-top: 10px" class="text-center">{{
            item.name
          }}</span>
          <span style="color: #777777"
            >฿{{ item.coursebranch.line_price }}</span
          >
        </van-grid-item>
      </van-grid>
    </div>
  </div>
</template>
